import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getPublicMenuApi,
  getPublicOffersApi,
  getCategoriesWithOffersApi,
  getMenuWithOffersApi,
  validateOfferCodeApi,
  applyOfferToCartApi,
  createWhatsAppOrderApi,
  getRestaurantApi,
  getAllRestaurantsApi
} from "../../api/publicMenuApi";

const initialState = {
  menu: [],
  restaurantInfo: null,
  restaurantDetails: null,
  menuLoading: false,
  menuError: null,

  offers: [],
  bogoOffers: [],
  discountOffers: [],
  freeItemOffers: [],
  categoryOffers: {},
  itemOffers: {},
  cartOffers: [],
  offersLoading: false,
  offersError: null,

  categoriesWithOffers: [],
  categoriesLoading: false,
  categoriesError: null,

  menuItemsWithOffers: [],
  menuItemsLoading: false,
  menuItemsError: null,

  validatedOffer: null,
  validatingOffer: false,
  validationError: null,

  cartOfferCalculation: {
    subtotal: 0,
    totalDiscount: 0,
    finalTotal: 0,
    appliedOffers: [],
    freeItems: [],
    savings: 0,
    itemDiscounts: 0,
    bogoDiscount: 0,
    promoDiscount: 0,
  },
  calculatingOffers: false,
  calculationError: null,

  orderResponse: null,
  placingOrder: false,
  orderError: null,

   restaurants: [],
  restaurantsLoading: false,
  restaurantsError: null,

  restaurantsPagination: {
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  },

  success: null,
  error: null,

  restaurantDetailsLoading: false,
  restaurantDetailsError: null,
};

export const fetchPublicMenu = createAsyncThunk(
  "public/fetchPublicMenu",
  async (restaurantId, { rejectWithValue }) => {
    try {
      const response = await getPublicMenuApi(restaurantId);
      // ✅ Check if response is successful
      if (response.data?.success === false) {
        return rejectWithValue({
          message: response.data?.message || "Failed to fetch menu",
          status: response.data?.data?.status || response.data?.status,
          data: response.data?.data || null,
          success: false,
          ...response.data,
        });
      }

      return {
        menu: response.data?.data || [],
        restaurant: response.data?.restaurant || {},
      };
    } catch (error) {
      // ✅ Handle error properly
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to fetch menu",
        status: error.response?.data?.data?.status || error.response?.status,
        data: error.response?.data?.data || null,
        success: error.response?.data?.success || false,
        ...error.response?.data,
      });
    }
  },
);

export const fetchPublicOffers = createAsyncThunk(
  "public/fetchPublicOffers",
  async (restaurantId, { rejectWithValue }) => {
    try {
      const response = await getPublicOffersApi(restaurantId);

      // ✅ Check if response is successful
      if (response.data?.success === false) {
        return rejectWithValue({
          message: response.data?.message || "Failed to fetch offers",
          status: response.data?.data?.status || response.data?.status,
          data: response.data?.data || null,
          success: false,
          ...response.data,
        });
      }

      const offersData = response.data?.data || {};
      const offersObj = offersData.offers || {};

      return {
        allOffers: offersObj.all || [],
        bogoOffers: offersObj.bogoOffers || [],
        discountOffers: offersObj.discountOffers || [],
        freeItemOffers: offersObj.freeItemOffers || [],
        cartOffers: offersObj.cartOffers || [],
        categoryOffers: offersData.categoryOfferMap || {},
        itemOffers: offersData.itemOfferMap || {},
        totalOffers: offersData.totalOffers || 0,
      };
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to fetch offers",
        status: error.response?.data?.data?.status || error.response?.status,
        data: error.response?.data?.data || null,
        success: error.response?.data?.success || false,
        ...error.response?.data,
      });
    }
  },
);
export const fetchCategoriesWithOffers = createAsyncThunk(
  "public/fetchCategoriesWithOffers",
  async (restaurantId, { rejectWithValue }) => {
    try {
      const response = await getCategoriesWithOffersApi(restaurantId);
      return response.data.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch categories",
      );
    }
  },
);

export const fetchMenuWithOffers = createAsyncThunk(
  "public/fetchMenuWithOffers",
  async (restaurantId, { rejectWithValue }) => {
    try {
      const response = await getMenuWithOffersApi(restaurantId);
      return response.data.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch menu items",
      );
    }
  },
);

export const validateOfferCode = createAsyncThunk(
  "public/validateOfferCode",
  async (
    { restaurantId, offerCode, cartItems, cartTotal },
    { rejectWithValue },
  ) => {
    try {
      const response = await validateOfferCodeApi({
        restaurantId,
        offerCode: offerCode.toUpperCase(),
        cartItems,
        cartTotal,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Invalid offer code",
      );
    }
  },
);

export const applyOfferToCart = createAsyncThunk(
  "public/applyOfferToCart",
  async (
    { restaurantId, cartItems, appliedOffers = [] },
    { rejectWithValue },
  ) => {
    try {
      const response = await applyOfferToCartApi({
        restaurantId,
        cartItems,
        appliedOffers,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to apply offers",
      );
    }
  },
);

export const createWhatsAppOrder = createAsyncThunk(
  "public/createWhatsAppOrder",
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await createWhatsAppOrderApi(orderData);
      return {
        ...response.data,
        orderId: response.data.orderId,
        whatsappUrl: response.data.whatsappUrl,
        order: response.data.order,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to place order",
      );
    }
  },
);

export const fetchRestaurantDetails = createAsyncThunk(
  "public/fetchRestaurantDetails",
  async (restaurantId, { rejectWithValue }) => {
    try {
      const response = await getRestaurantApi(restaurantId);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch restaurant details",
      );
    }
  },
);

// export const fetchAllRestaurants = createAsyncThunk(
//   "public/fetchAllRestaurants",
//   async ({ page = 1, limit = 8 } = {}, { rejectWithValue }) => {
//     try {
//       const response = await getAllRestaurantsApi(page, limit );

//       if (response.data?.success === false) {
//         return rejectWithValue(
//           response.data?.message || "Failed to fetch restaurants",
//         );
//       }

//       return response.data;
//     } catch (error) {
//       return rejectWithValue(
//         error.response?.data?.message ||
//           "Failed to fetch restaurants",
//       );
//     }
//   },
// );


// SLICE

export const fetchAllRestaurants = createAsyncThunk(
  "public/fetchAllRestaurants",
  async (
    { page = 1, limit = 8, search = "", state = "", city = "" } = {},
    { rejectWithValue },
  ) => {
    try {
      const response = await getAllRestaurantsApi(
        page,
        limit,
        search,
        state,
        city,
      );

      if (response.data?.success === false) {
        return rejectWithValue(
          response.data?.message || "Failed to fetch restaurants",
        );
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch restaurants",
      );
    }
  },
);


const publicSlice = createSlice({
  name: "public",
  initialState,
  reducers: {
    clearPublicState: (state) => {
      state.success = null;
      state.error = null;
      state.validationError = null;
      state.orderError = null;
    },
    clearValidatedOffer: (state) => {
      state.validatedOffer = null;
      state.validationError = null;
    },
    clearCartCalculation: (state) => {
      state.cartOfferCalculation = {
        subtotal: 0,
        totalDiscount: 0,
        finalTotal: 0,
        appliedOffers: [],
        freeItems: [],
        savings: 0,
        itemDiscounts: 0,
        bogoDiscount: 0,
        promoDiscount: 0,
      };
    },
    resetOrderState: (state) => {
      state.orderResponse = null;
      state.orderError = null;
      state.placingOrder = false;
    },
    clearOrderResponse: (state) => {
      state.orderResponse = null;
    },
    setRestaurantInfo: (state, action) => {
      state.restaurantInfo = action.payload;
    },
    updateCartCalculation: (state, action) => {
      state.cartOfferCalculation = {
        ...state.cartOfferCalculation,
        ...action.payload,
      };
    },
    resetMenuError: (state) => {
      state.menuError = null;
      state.error = null;
    },
    resetPublicState: (state) => {
      state.menu = [];
      state.restaurantInfo = null;
      state.restaurantDetails = null;
      state.menuLoading = false;
      state.menuError = null;
      state.offers = [];
      state.bogoOffers = [];
      state.discountOffers = [];
      state.freeItemOffers = [];
      state.categoryOffers = {};
      state.itemOffers = {};
      state.cartOffers = [];
      state.offersLoading = false;
      state.offersError = null;
      state.error = null;
      state.success = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // ── 1. Public Menu ──
      .addCase(fetchPublicMenu.pending, (s) => {
        s.menuLoading = true;
        s.menuError = null;
      })
      .addCase(fetchPublicMenu.fulfilled, (s, a) => {
        s.menuLoading = false;
        s.menu = a.payload.menu;
        s.restaurantInfo = a.payload.restaurant;
        s.menuError = null;
      })
      .addCase(fetchPublicMenu.rejected, (s, a) => {
        s.menuLoading = false;
        s.menuError = a.payload;
        s.error = a.payload;
      })

      // ── 2. Public Offers ──
      .addCase(fetchPublicOffers.pending, (s) => {
        s.offersLoading = true;
        s.offersError = null;
      })
      .addCase(fetchPublicOffers.fulfilled, (s, a) => {
        s.offersLoading = false;
        s.offers = a.payload.allOffers;
        s.bogoOffers = a.payload.bogoOffers;
        s.discountOffers = a.payload.discountOffers;
        s.freeItemOffers = a.payload.freeItemOffers;
        s.cartOffers = a.payload.cartOffers;
        s.categoryOffers = a.payload.categoryOffers;
        s.itemOffers = a.payload.itemOffers;
      })
      .addCase(fetchPublicOffers.rejected, (s, a) => {
        s.offersLoading = false;
        s.offersError = a.payload;
        s.error = a.payload;
      })
      // ── 3. Categories with Offers ──
      .addCase(fetchCategoriesWithOffers.pending, (s) => {
        s.categoriesLoading = true;
        s.categoriesError = null;
      })
      .addCase(fetchCategoriesWithOffers.fulfilled, (s, a) => {
        s.categoriesLoading = false;
        s.categoriesWithOffers = a.payload;
      })
      .addCase(fetchCategoriesWithOffers.rejected, (s, a) => {
        s.categoriesLoading = false;
        s.categoriesError = a.payload;
        s.error = a.payload;
      })

      // ── 4. Menu Items with Offers ──
      .addCase(fetchMenuWithOffers.pending, (s) => {
        s.menuItemsLoading = true;
        s.menuItemsError = null;
      })
      .addCase(fetchMenuWithOffers.fulfilled, (s, a) => {
        s.menuItemsLoading = false;
        s.menuItemsWithOffers = a.payload;
      })
      .addCase(fetchMenuWithOffers.rejected, (s, a) => {
        s.menuItemsLoading = false;
        s.menuItemsError = a.payload;
        s.error = a.payload;
      })

      // ── 5. Validate Offer Code ──
      .addCase(validateOfferCode.pending, (s) => {
        s.validatingOffer = true;
        s.validationError = null;
      })
      .addCase(validateOfferCode.fulfilled, (s, a) => {
        s.validatingOffer = false;
        s.validatedOffer = a.payload;
        s.success = "Offer applied successfully!";
      })
      .addCase(validateOfferCode.rejected, (s, a) => {
        s.validatingOffer = false;
        s.validationError = a.payload;
        s.error = a.payload;
      })

      // ── 6. Apply Offer to Cart ──
      .addCase(applyOfferToCart.pending, (s) => {
        s.calculatingOffers = true;
        s.calculationError = null;
      })
      // .addCase(applyOfferToCart.fulfilled, (s, a) => {
      //   s.calculatingOffers = false;

      //   // Calculate breakdown
      //   const itemDiscounts = a.payload.itemDiscounts || 0;
      //   const bogoDiscount = a.payload.bogoDiscount || 0;
      //   const promoDiscount = a.payload.promoDiscount || 0;
      //   const totalDiscount = itemDiscounts + bogoDiscount + promoDiscount;

      //   s.cartOfferCalculation = {
      //     subtotal: a.payload.subtotal || 0,
      //     totalDiscount: totalDiscount,
      //     finalTotal: a.payload.finalTotal || 0,
      //     appliedOffers: a.payload.appliedOffers || [],
      //     freeItems: a.payload.freeItems || [],
      //     savings: totalDiscount,
      //     itemDiscounts: itemDiscounts,
      //     bogoDiscount: bogoDiscount,
      //     promoDiscount: promoDiscount,
      //   };
      // })
      .addCase(applyOfferToCart.fulfilled, (s, a) => {
  s.calculatingOffers = false;

  const subtotal = a.payload.subtotal || 0;
  const totalDiscount = a.payload.totalDiscount || 0;
  const finalTotal = a.payload.finalTotal || 0;
  const savings = a.payload.savings || 0;

  // 🔥 Backend se alag-alag values lo
  const itemDiscounts = a.payload.itemDiscounts || 0;
  const bogoDiscount = a.payload.bogoDiscount || 0;
  const promoDiscount = a.payload.promoDiscount || 0;

  s.cartOfferCalculation = {
    subtotal,
    totalDiscount,
    finalTotal,
    appliedOffers: a.payload.appliedOffers || [],
    freeItems: a.payload.freeItems || [],
    savings: savings || totalDiscount,
    itemDiscounts,
    bogoDiscount,
    promoDiscount,
  };
})
      // .addCase(applyOfferToCart.fulfilled, (s, a) => {
      //   s.calculatingOffers = false;

      //   // 🔥 Backend se values lo
      //   const subtotal = a.payload.subtotal || 0;
      //   const totalDiscount = a.payload.totalDiscount || 0;
      //   const finalTotal = a.payload.finalTotal || 0;
      //   const savings = a.payload.savings || 0;

      //   // 🔥 Individual discounts — agar backend deta hai to use karo,
      //   // warna totalDiscount se derive karo
      //   const itemDiscounts = a.payload.itemDiscounts || 0;
      //   const bogoDiscount = a.payload.bogoDiscount || 0;

      //   // 🔥 promoDiscount = totalDiscount - itemDiscounts - bogoDiscount
      //   let promoDiscount = a.payload.promoDiscount || 0;
      //   if (promoDiscount === 0 && totalDiscount > 0) {
      //     promoDiscount = Math.max(
      //       0,
      //       totalDiscount - itemDiscounts - bogoDiscount,
      //     );
      //   }

      //   console.log("🎟️ applyOfferToCart — parsed:", {
      //     subtotal,
      //     totalDiscount,
      //     finalTotal,
      //     itemDiscounts,
      //     bogoDiscount,
      //     promoDiscount,
      //   });

      //   s.cartOfferCalculation = {
      //     subtotal,
      //     totalDiscount,
      //     finalTotal,
      //     appliedOffers: a.payload.appliedOffers || [],
      //     freeItems: a.payload.freeItems || [],
      //     savings: savings || totalDiscount,
      //     itemDiscounts,
      //     bogoDiscount,
      //     promoDiscount,
      //   };
      // })
      .addCase(applyOfferToCart.rejected, (s, a) => {
        s.calculatingOffers = false;
        s.calculationError = a.payload;
        s.error = a.payload;
      })
      // 7 create WhatsApp Order
      .addCase(createWhatsAppOrder.pending, (s) => {
        s.placingOrder = true;
        s.orderError = null;
      })
      .addCase(createWhatsAppOrder.fulfilled, (s, a) => {
        s.placingOrder = false;
        s.orderResponse = a.payload;
        s.success = "Order placed successfully!";
      })
      .addCase(createWhatsAppOrder.rejected, (s, a) => {
        s.placingOrder = false;
        s.orderError = a.payload;
        s.error = a.payload;
      })
      // ── 8. Fetch Restaurant Details ──
      .addCase(fetchRestaurantDetails.pending, (s) => {
        s.restaurantDetailsLoading = true;
        s.restaurantDetailsError = null;
      })
      .addCase(fetchRestaurantDetails.fulfilled, (s, a) => {
        s.restaurantDetailsLoading = false;
        s.restaurantDetails = a.payload;
      })
      .addCase(fetchRestaurantDetails.rejected, (s, a) => {
        s.restaurantDetailsLoading = false;
        s.restaurantDetailsError = a.payload;
        s.error = a.payload;
      })

            // ── 9. Fetch All Restaurants ──
      .addCase(fetchAllRestaurants.pending, (s) => {
        s.restaurantsLoading = true;
        s.restaurantsError = null;
      })

      .addCase(fetchAllRestaurants.fulfilled, (s, a) => {
        s.restaurantsLoading = false;

        // Backend response ke according
        s.restaurants = a.payload?.data || [];

        const pagination = a.payload?.pagination || {};

        s.restaurantsPagination = {
          page: pagination.page || 1,
          limit: pagination.limit || 12,
          total: pagination.total || 0,
          totalPages: pagination.totalPages || 0,
          hasNextPage: pagination.hasNextPage || false,
          hasPrevPage: pagination.hasPrevPage || false,
        };

        s.restaurantsError = null;
      })

      .addCase(fetchAllRestaurants.rejected, (s, a) => {
        s.restaurantsLoading = false;
        s.restaurantsError = a.payload || "Failed to fetch restaurants";
        s.error = a.payload || "Failed to fetch restaurants";
      })
  },
});

// ========== SELECTORS ==========
export const selectPublicMenu = (s) => s.public.menu;
export const selectRestaurantInfo = (s) => s.public.restaurantInfo;
export const selectMenuLoading = (s) => s.public.menuLoading;
export const selectMenuError = (s) => s.public.menuError;

export const selectAllOffers = (s) => s.public.offers;
export const selectBogoOffers = (s) => s.public.bogoOffers;
export const selectDiscountOffers = (s) => s.public.discountOffers;
export const selectFreeItemOffers = (s) => s.public.freeItemOffers;
export const selectCategoryOffers = (s) => s.public.categoryOffers;
export const selectItemOffers = (s) => s.public.itemOffers;
export const selectCartOffers = (s) => s.public.cartOffers;
export const selectOffersLoading = (s) => s.public.offersLoading;

export const selectCategoriesWithOffers = (s) => s.public.categoriesWithOffers;
export const selectMenuItemsWithOffers = (s) => s.public.menuItemsWithOffers;

export const selectValidatedOffer = (s) => s.public.validatedOffer;
export const selectValidatingOffer = (s) => s.public.validatingOffer;

export const selectCartOfferCalculation = (s) => s.public.cartOfferCalculation;
export const selectCalculatingOffers = (s) => s.public.calculatingOffers;
export const selectItemDiscounts = (s) =>
  s.public.cartOfferCalculation.itemDiscounts;
export const selectBogoDiscount = (s) =>
  s.public.cartOfferCalculation.bogoDiscount;
export const selectPromoDiscount = (s) =>
  s.public.cartOfferCalculation.promoDiscount;
export const selectFreeItems = (s) => s.public.cartOfferCalculation.freeItems;

export const selectOrderResponse = (s) => s.public.orderResponse;
export const selectPlacingOrder = (s) => s.public.placingOrder;

export const selectPublicError = (s) => s.public.error;
export const selectPublicSuccess = (s) => s.public.success;

export const selectRestaurantDetails = (s) => s.public.restaurantDetails;
export const selectRestaurantDetailsLoading = (s) =>
  s.public.restaurantDetailsLoading;
export const selectRestaurantDetailsError = (s) =>
  s.public.restaurantDetailsError;

export const selectAllRestaurants = (s) => s.public.restaurants;

export const selectRestaurantsLoading = (s) =>
  s.public.restaurantsLoading;

export const selectRestaurantsError = (s) =>
  s.public.restaurantsError;

export const selectRestaurantsPagination = (s) =>
  s.public.restaurantsPagination;

// ========== ACTIONS ==========
export const {
  clearPublicState,
  clearValidatedOffer,
  clearCartCalculation,
  resetOrderState,
  setRestaurantInfo,
  updateCartCalculation,
  resetMenuError,
  resetPublicState,
  clearOrderResponse,
} = publicSlice.actions;

export default publicSlice.reducer;
